import React, { useCallback, useContext } from "react";
import PropTypes from "prop-types";
import { ThemeContext } from "styled-components";

import { CrosswordSizeContext } from "./context";

const cellPropTypes = {
	/** the data specific to this cell */
	cellData: PropTypes.shape({
		row: PropTypes.number.isRequired,
		col: PropTypes.number.isRequired,
		guess: PropTypes.string, // .isRequired,
		number: PropTypes.string,
		answer: PropTypes.string,
	}).isRequired,

	/** whether this cell has focus */
	focus: PropTypes.bool,

	/** whether this cell is highlighted */
	highlight: PropTypes.bool,

	/** handler called when the cell is clicked */
	onClick: PropTypes.func,
};

/**
 * An individual-letter answer cell within the crossword grid.
 *
 * A `Cell` lives inside the SVG for a
 * [`CrosswordGrid`](#/Complex%20layouts/CrosswordGrid), and renders at a
 * position determined by the `row`, `col`, and `cellSize` properties from
 * `cellData` and `renderContext`.
 */
export default function Cell({ cellData, onClick, focus, highlight }) {
	const { cellSize, cellPadding, cellInner, cellHalf, fontSize } = useContext(CrosswordSizeContext);
	const {
		// gridBackground,
		cellBackground,
		cellBorder,
		textColor,
		numberColor,
		focusBackground,
		highlightBackground,
	} = useContext(ThemeContext);

	const handleClick = useCallback(
		(event) => {
			event.preventDefault();
			if (onClick) {
				onClick(cellData);
			}
		},
		[cellData, onClick]
	);

	const { row, col, guess, number, completed, corrected, gameCompletion, attempted } = cellData;

	const x = col * cellSize;
	const y = row * cellSize;

	return (
		<g onClick={handleClick} style={{ cursor: "default", fontSize: `${fontSize}px` }} className="clue-cell">
			<rect
				x={x + cellPadding}
				y={y + cellPadding}
				width={cellInner}
				height={cellInner}
				fill={focus ? focusBackground : highlight ? highlightBackground : cellBackground}
				stroke={cellBorder}
				strokeWidth={cellSize / 50}
				rx="2px"
			/>
			{number && (
				<text x={x + cellPadding * 4} y={y + cellPadding * 4} textAnchor="start" dominantBaseline="hanging" style={{ fontSize: "50%", fill: numberColor }}>
					{number}
				</text>
			)}
			<text
				x={x + cellHalf}
				y={y + cellHalf + 1} // +1 for visual alignment?
				textAnchor="middle"
				dominantBaseline="middle"
				style={{ fill: textColor, cursor: corrected && completed && "not-allowed" }}
				className={
					gameCompletion ? (attempted ? (corrected ? "guess-text-correct" : "guess-text-incorrect") : "") : corrected ? (completed ? "guess-text-correct" : "guess-text-incorrect") : ""
				}
			>
				{guess}
			</text>
		</g>
	);
}

Cell.propTypes = cellPropTypes;

Cell.defaultProps = {
	focus: false,
	highlight: false,
	onClick: null,
};

// export default Cell;
